import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { COMPANY, buildSignature, SIGN_TITLE_EXAMPLE } from '../lib/company.js';
import { FLOWS, PRINCIPLES, DRAFT_NOTE } from '../lib/handbook.js';

// หน้า "วิธีทำงาน" — ที่ที่พนักงานใหม่เปิดอ่านแล้วทำงานต่อได้เองโดยไม่ต้องรอใครสอน
//
// ⚠ ทำไมอยู่ "ในแอพ" ไม่ใช่ในคู่มือ /guide:
//   /guide เป็นไฟล์ static ที่เปิดอ่านได้โดยไม่ต้องล็อกอิน (ตรวจแล้ว: ตอบ 200 ให้คนที่ไม่มีบัญชี)
//   ชื่อ/ที่อยู่/เลขภาษีเป็นข้อมูลสาธารณะอยู่แล้ว (อยู่บนใบกำกับทุกใบ) วางที่ไหนก็ได้
//   แต่ขั้นตอนงานภายในมีชื่อ forwarder · เพดานภาษีที่ต้องจ่ายก่อน · วิธีตรวจราคา — ของพวกนี้
//   ไม่ควรเปิดให้คนนอกอ่าน จึงต้องอยู่หลังการล็อกอิน
//
// เปิดได้ทุกสิทธิ์ (รวมคลัง) — ไม่มีต้นทุนหรือตัวเลขราคาในหน้านี้

function CopyRow({ label, value, mono }) {
  const [hit, setHit] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setHit(true);
      setTimeout(() => setHit(false), 1400);
    } catch {
      // เบราว์เซอร์ไม่ให้สิทธิ์คลิปบอร์ด (http หรือปิดไว้) — ให้ผู้ใช้ลากคลุมเอง
      setHit(null);
    }
  };
  return (
    <div className="hb-row">
      <div className="hb-rl">{label}</div>
      <div className={`hb-rv ${mono ? 'mono' : ''}`}>{value}</div>
      <button className="hb-copy" onClick={copy}
        title={`คัดลอก${label}`} aria-label={`คัดลอก${label}`}>
        {hit === true ? '✓ คัดลอกแล้ว' : hit === null ? 'ลากคลุมเพื่อคัดลอก' : '⧉ คัดลอก'}
      </button>
    </div>
  );
}

// ลายเซ็นท้ายอีเมลของ "คนที่กำลังเปิดหน้านี้" — ไม่ใช่ตัวอย่างให้ไปแก้เอง
// บล็อกบริษัทตายตัว ส่วนตัวเองกรอก 4 ช่อง แล้วกดคัดลอกไปวางในโปรแกรมอีเมลได้เลย
//
// เก็บใน localStorage ของเครื่องตัวเอง ไม่ขึ้นฐานข้อมูล:
//   ① เป็นข้อมูลของคนคนเดียว ไม่มีใครอื่นต้องอ่าน  ② ไม่ต้องรอ migration
//   ③ เปลี่ยนเครื่องแล้วกรอกใหม่ 4 ช่อง ไม่ใช่ภาระ
// ⚠ try/catch ทุกจุด — โหมดส่วนตัวของเบราว์เซอร์ throw ตั้งแต่ตอนอ่าน
const SIG_KEY = 'ib_sig_v1';
const SIG_EMPTY = { name: '', title: '', phone: '', email: '' };

function Signature() {
  const { session } = useAuth();
  const myEmail = session?.user?.email ?? '';
  const [f, setF] = useState(() => {
    try { return { ...SIG_EMPTY, ...JSON.parse(localStorage.getItem(SIG_KEY) || '{}') }; }
    catch { return { ...SIG_EMPTY }; }
  });
  const [copied, setCopied] = useState(false);

  // เติมอีเมลที่ใช้ล็อกอินให้ครั้งแรก — ของบริษัทอยู่แล้ว ไม่ต้องพิมพ์ซ้ำ
  useEffect(() => {
    if (myEmail && !f.email) setF(v => ({ ...v, email: myEmail }));
  }, [myEmail]);

  useEffect(() => {
    try { localStorage.setItem(SIG_KEY, JSON.stringify(f)); } catch { /* โหมดส่วนตัว */ }
  }, [f]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const text = buildSignature(f);
  const ready = f.name.trim() && f.title.trim() && f.phone.trim() && f.email.trim();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { setCopied(null); }
  };

  return (
    <>
      <div className="hb-card">
        <div className="hb-ct">กรอกของตัวเอง 4 ช่อง — บล็อกบริษัทข้างล่างใส่ให้แล้ว</div>
        <div className="hb-sgrid">
          <label className="hb-fld">
            <span>ชื่อ–นามสกุล ภาษาอังกฤษ</span>
            <input value={f.name} onChange={set('name')} placeholder="Wutthichai Kiattanongsak"/>
          </label>
          <label className="hb-fld">
            <span>ตำแหน่ง</span>
            <input value={f.title} onChange={set('title')} placeholder={SIGN_TITLE_EXAMPLE}/>
          </label>
          <label className="hb-fld">
            <span>เบอร์โทร <i>ขึ้นต้น +66(0) ให้แล้ว — กรอกเฉพาะเลขที่เหลือ</i></span>
            <input value={f.phone} onChange={set('phone')} placeholder="81-234-5678"/>
          </label>
          <label className="hb-fld">
            <span>อีเมลบริษัทของตัวเอง</span>
            <input value={f.email} onChange={set('email')} placeholder="name@campstudio.co.th"/>
          </label>
        </div>
      </div>

      <div className="hb-card">
        <div className="hb-ct">ลายเซ็นที่ได้ — กดคัดลอกไปวางท้ายอีเมลได้เลย</div>
        <pre className={`hb-sig ${ready ? '' : 'part'}`}>{text}</pre>
        <div className="hb-sfoot">
          <button className="hb-copy" onClick={copy}>
            {copied === true ? '✓ คัดลอกแล้ว' : copied === null ? 'ลากคลุมเพื่อคัดลอก' : '⧉ คัดลอกลายเซ็น'}
          </button>
          {!ready && <span className="hb-swarn">ช่องใน [ ] คือที่ยังไม่ได้กรอก</span>}
        </div>
      </div>

      <div className="hb-note tip">
        เก็บไว้ในเครื่องนี้เครื่องเดียว ไม่ได้ส่งขึ้นระบบ — เปลี่ยนเครื่องหรือล้างข้อมูลเบราว์เซอร์แล้วกรอกใหม่ 4 ช่อง
      </div>
    </>
  );
}

function Flow({ f }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`hb-flow k-${f.cat} ${open ? 'on' : ''}`}>
      <button className="hb-fh" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="hb-fno">{f.no}</span>
        <span className="hb-ft">
          <b>{f.title}</b>
          <em>{f.scope}</em>
        </span>
        <span className="hb-fn">{f.steps.length} ขั้น</span>
        <span className="hb-fx">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="hb-fb">
          {f.frame && (
            <div className="hb-frame">
              <div className="hb-frl">ลำดับความคิดเวลาอ่านอีเมล</div>
              <div className="hb-frs">
                {f.frame.map((x, i) => (
                  <span key={x}>{x}{i < f.frame.length - 1 && <i> → </i>}</span>
                ))}
              </div>
            </div>
          )}

          {f.steps.map(s => (
            <div key={s.n} className="hb-step">
              <div className="hb-sn">{s.n}</div>
              <div className="hb-sb">
                <div className="hb-when">{s.when}</div>
                <div className="hb-act">{s.act}</div>

                {s.check && (
                  <div className="hb-f"><span className="hb-fl">ต้องตรวจ</span>{s.check}</div>
                )}
                <div className="hb-f done"><span className="hb-fl">ถือว่าเสร็จเมื่อ</span>{s.done}</div>
                {s.note && (
                  <div className="hb-f warn"><span className="hb-fl">ระวัง</span>{s.note}</div>
                )}
                {s.here && (
                  <div className="hb-here"><b>ขั้นนี้ทำในแอพนี้</b> — {s.here}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Handbook() {
  return (
    <div className="hb">
      <div className="page-h">
        <h2>วิธีทำงาน</h2>
        <div className="meta">ข้อมูลบริษัทที่ถูกต้อง · ขั้นตอนงาน · หลักการที่ตกลงกันไว้</div>
      </div>

      <div className="hb-lead">
        เปิดหน้านี้ได้ทุกเมื่อ ไม่ต้องจำ — ที่อยู่บริษัทที่ถูกต้องสำหรับใส่ในอีเมลและเอกสาร
        อยู่ด้านล่างนี้ และขั้นตอนงานทั้ง 4 สายอยู่ถัดไป
      </div>

      {/* ── ข้อมูลบริษัท ─────────────────────────────────────────── */}
      <div className="hb-sec">
        <div className="hb-sh">
          <span className="hb-shn">ก</span>
          <b>ข้อมูลบริษัท</b>
          <em>ตรงกับทะเบียนจริง — ใช้ชุดนี้ทุกครั้งที่ต้องกรอกให้ supplier หรือ forwarder</em>
        </div>

        <div className="hb-card">
          <div className="hb-ct">ภาษาไทย</div>
          <CopyRow label="ชื่อบริษัท" value={`${COMPANY.legalTh} (${COMPANY.branchTh})`}/>
          <CopyRow label="ที่อยู่" value={COMPANY.addrTh}/>
        </div>

        <div className="hb-card">
          <div className="hb-ct">English — ใช้กับ supplier ต่างประเทศ เอกสารขนส่ง และใบขนสินค้า</div>
          <CopyRow label="Company" value={COMPANY.legalEn}/>
          <CopyRow label="Address" value={`${COMPANY.addrEn} (${COMPANY.branchEn})`}/>
          <div className="hb-hint">
            ที่อยู่ชุดนี้ใช้<b>ทุกที่</b> — ทั้งอีเมล เอกสารขนส่ง ใบขนสินค้า และลายเซ็นท้ายอีเมล (หัวข้อ ข)
            สะกดเหมือนกันทุกตัวอักษร ไม่ต้องจำสองแบบ
          </div>
        </div>

        <div className="hb-card">
          <div className="hb-ct">ติดต่อ & เลขทะเบียน</div>
          <CopyRow label="โทรศัพท์" value={COMPANY.telTh} mono/>
          <CopyRow label="Tel (รูปแบบสากล)" value={COMPANY.tel} mono/>
          <CopyRow label="เลขผู้เสียภาษี / Tax ID" value={COMPANY.taxId} mono/>
          <CopyRow label="อีเมลบริษัท" value={COMPANY.email} mono/>
        </div>

        <div className="hb-note tip">
          เอกสารที่พิมพ์ออกจากแอพนี้ (เอกสารประกอบการตีพิกัด · ใบขออนุญาต · สรุปข้อมูลแบรนด์)
          ใช้ที่อยู่ชุดเดียวกันนี้อยู่แล้วโดยอัตโนมัติ ไม่ต้องพิมพ์เอง
        </div>
      </div>

      {/* ── วิธีลงท้ายอีเมล ───────────────────────────────────────── */}
      <div className="hb-sec">
        <div className="hb-sh">
          <span className="hb-shn">ข</span>
          <b>วิธีลงท้ายอีเมล</b>
          <em>รูปแบบเดียวกันทุกคน — กรอกชื่อตัวเองแล้วกดคัดลอกไปใช้ได้เลย</em>
        </div>

        <Signature/>
      </div>

      {/* ── หลักการ ───────────────────────────────────────────────── */}
      <div className="hb-sec">
        <div className="hb-sh">
          <span className="hb-shn">ค</span>
          <b>หลักการที่ตกลงกันไว้</b>
          <em>4 ข้อนี้ใช้ได้กับงานทุกสาย</em>
        </div>

        <div className="hb-prs">
          {PRINCIPLES.map(p => (
            <div key={p.t} className="hb-pr">
              <div className="hb-prt">{p.t}</div>
              <div className="hb-prd">{p.d}</div>
              <div className="hb-prs2">{p.src}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── ขั้นตอนงาน ────────────────────────────────────────────── */}
      <div className="hb-sec">
        <div className="hb-sh">
          <span className="hb-shn">ง</span>
          <b>ขั้นตอนงาน 4 สาย</b>
          <em>กดที่หัวข้อเพื่อดูทีละขั้น</em>
        </div>

        <div className="hb-note warn">
          <b>ร่าง ยังไม่ได้อนุมัติ</b> — {DRAFT_NOTE}
        </div>

        {FLOWS.map(f => <Flow key={f.id} f={f}/>)}

        <div className="hb-src">
          ที่มา: ไฟล์ส่งมอบงานผู้ประสานงานต่างประเทศ ฉบับ v2-1 — ชีต Process ทั้ง 4 ชีต
          (ผู้เขียน: ปรินทร อุษณวศิน) · แก้เฉพาะคำที่พิมพ์ผิด เนื้อความไม่เปลี่ยน
          และไม่ได้เติมขั้นตอนที่ชีตไม่มี
        </div>
      </div>
    </div>
  );
}
